/* ==========================================================================
   boutiq.experiences — "Arlberg Richtung" sections
   One job: a set of pickers (village pins, range profiles) that each show
   one panel. All panels are rendered by Liquid; this only switches which is
   visible. Without JavaScript the first panel stays visible.
   ========================================================================== */

(() => {
  'use strict';

  if (customElements.get('pick-panels')) return;

  class PickPanels extends HTMLElement {
    connectedCallback() {
      this.pickers = Array.from(this.querySelectorAll('[data-pick]'));
      this.panels = Array.from(this.querySelectorAll('[data-panel]'));
      if (!this.pickers.length) return;

      this.pickers.forEach((el, index) => {
        el.addEventListener('click', () => this.select(el.dataset.pick));
        el.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.select(el.dataset.pick);
          } else if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
            event.preventDefault();
            const step = event.key === 'ArrowRight' ? 1 : -1;
            const next = this.pickers[(index + step + this.pickers.length) % this.pickers.length];
            next.focus();
            this.select(next.dataset.pick);
          }
        });
      });

      // After an enquiry was sent from a panel, the page reloads: open that
      // panel again so the thank-you (or the error) is in view.
      const answered = this.querySelector('.ranges__form [role="status"], .ranges__form [role="alert"]');
      const answeredPanel = answered?.closest('[data-panel]');
      const initial =
        (answeredPanel && this.pickers.find((el) => el.dataset.pick === answeredPanel.dataset.panel)) ||
        this.pickers.find((el) => el.getAttribute('aria-pressed') === 'true') ||
        this.pickers[0];
      this.select(initial.dataset.pick);
      if (answeredPanel) requestAnimationFrame(() => answeredPanel.scrollIntoView({ block: 'center' }));
    }

    select(key) {
      this.pickers.forEach((el) => el.setAttribute('aria-pressed', String(el.dataset.pick === key)));
      this.panels.forEach((panel) => { panel.hidden = panel.dataset.panel !== key; });
    }
  }

  customElements.define('pick-panels', PickPanels);

  // Theme editor: selecting a block in the sidebar shows its panel.
  document.addEventListener('shopify:block:select', (event) => {
    const host = event.target.closest('pick-panels');
    const key = event.target.dataset?.pick || event.target.dataset?.panel;
    if (host && key) host.select(key);
  });
})();

/* ==========================================================================
   Header: compact once the page scrolls, and the full-screen menu on small
   screens (opens like a curtain, closes with the cross, Escape or a link).
   ========================================================================== */

(() => {
  'use strict';

  const header = document.querySelector('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  let lastFocus = null;

  const panelFor = (toggle) => document.getElementById(toggle.getAttribute('aria-controls')) || document.querySelector('[data-mnav]');
  const togglesFor = (panel) => document.querySelectorAll(`[data-mnav-toggle][aria-controls="${panel.id}"]`);

  const open = (panel) => {
    lastFocus = document.activeElement;
    panel.hidden = false;
    // one frame with the panel in the layout, so the curtain can run
    requestAnimationFrame(() => requestAnimationFrame(() => panel.classList.add('is-open')));
    document.documentElement.classList.add('mnav-open');
    togglesFor(panel).forEach((t) => t.setAttribute('aria-expanded', 'true'));
    setTimeout(() => panel.querySelector('.mnav__link, [data-mnav-close]')?.focus({ preventScroll: true }), reduced.matches ? 50 : 320);
  };

  const close = (panel) => {
    if (!panel || panel.hidden) return;
    panel.classList.remove('is-open');
    document.documentElement.classList.remove('mnav-open');
    togglesFor(panel).forEach((t) => t.setAttribute('aria-expanded', 'false'));
    const done = () => { if (!panel.classList.contains('is-open')) panel.hidden = true; };
    setTimeout(done, reduced.matches ? 220 : 580);
    lastFocus?.focus?.({ preventScroll: true });
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-mnav-toggle]');
    if (toggle) {
      const panel = panelFor(toggle);
      if (!panel) return;
      panel.classList.contains('is-open') ? close(panel) : open(panel);
      return;
    }
    const panel = event.target.closest('[data-mnav]');
    if (!panel) return;
    if (event.target.closest('[data-mnav-close]') || event.target.closest('a[href]')) close(panel);
  });

  document.addEventListener('keydown', (event) => {
    const panel = document.querySelector('[data-mnav].is-open');
    if (!panel) return;
    if (event.key === 'Escape') { close(panel); return; }
    if (event.key !== 'Tab') return;
    // keep the focus inside the open menu
    const items = Array.from(panel.querySelectorAll('a[href], button:not([disabled])')).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  // Widening the window past the breakpoint must not leave a locked page behind.
  window.addEventListener('resize', () => {
    const panel = document.querySelector('[data-mnav].is-open');
    const burger = document.querySelector('.header__burger');
    if (panel && burger && getComputedStyle(burger).display === 'none') close(panel);
  });
})();

/* ==========================================================================
   Accordions filled in later (e.g. the FAQ split from a metafield) miss the
   "one open at a time" wiring in theme.js, which runs once at start. One
   listener on the document covers every group, early or late.
   ========================================================================== */
document.addEventListener('toggle', (event) => {
  const item = event.target;
  if (!(item instanceof HTMLDetailsElement) || !item.open) return;
  const group = item.closest('[data-accordion="single"]');
  if (!group) return;
  group.querySelectorAll('details[open]').forEach((other) => { if (other !== item) other.open = false; });
}, true);

/* The sticky jump bar on product pages sits under the sticky header. */
(() => {
  const header = document.querySelector('[data-header]');
  if (!header) return;
  const set = () => document.documentElement.style.setProperty('--header-h', `${header.offsetHeight}px`);
  set();
  window.addEventListener('resize', set, { passive: true });
  if ('ResizeObserver' in window) new ResizeObserver(set).observe(header);
})();
